/**
 * 클립보드에 글자 넣기 — 콘솔의 [복사] 단추가 모두 이것을 쓴다.
 *
 * **평문 http 에서도 복사된다(2026-10-27).** `navigator.clipboard` 는 보안 컨텍스트(https·localhost)에만 있다.
 * 고객사 서버는 대개 `http://<서버 IP>:4600` 으로 열어 그 객체가 없고, `navigator.clipboard?.writeText(…)` 는 오류도
 * 없이 아무 일도 하지 않았다 — .79 설치 서버에서 통합 관제 연결 토큰의 [복사] 가 그랬다(운영은 https 라 몰랐다).
 * 그럴 때는 화면 밖 글상자에 넣고 골라 `document.execCommand('copy')` 로 복사한다. 낡은 API 지만 비보안
 * 컨텍스트에서 되는 유일한 길이고, 주요 브라우저가 모두 아직 지원한다.
 *
 * 누른 그 자리(클릭 처리 안)에서 부른다 — 브라우저는 사용자의 동작 없이 하는 복사를 막는다.
 * 못 넣으면 false 다. 부르는 쪽은 "복사됨" 을 띄우지 않고, 글자는 화면에 보이는 그대로 둔다(손으로 고를 수 있게).
 *
 * @param from 누른 단추. 모달 안이면 글상자를 그 안에 붙인다 — 초점이 창 밖으로 나가지 않게.
 */
export async function copyText(text: string, from?: Element | null): Promise<boolean> {
  if (globalThis.isSecureContext && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // 권한이 막힌 브라우저 — 아래 길로 한 번 더 해 본다.
    }
  }
  return copyBySelection(text, from);
}

/** 글상자를 잠깐 붙여 고르고 복사한다. */
function copyBySelection(text: string, from?: Element | null): boolean {
  const host = from?.closest('[aria-modal="true"]') ?? document.body;
  const previous = document.activeElement as HTMLElement | null;
  const box = document.createElement('textarea');
  box.value = text;
  box.setAttribute('readonly', '');
  // 보이지 않게 두되 display:none 은 고를 수 없어 안 된다. 글자는 12pt — iOS 가 화면을 확대하지 않게.
  box.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:0;opacity:0;font-size:12pt';
  host.appendChild(box);
  try {
    box.focus({ preventScroll: true });
    box.select();
    box.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    box.remove();
    previous?.focus?.({ preventScroll: true });
  }
}
